/*
 * Top-up button for the cart drawer's offer cards — the upsell "Add" and the
 * bulk tier "Get N". Both raise the cart to a target quantity rather than
 * adding on top of what is already there.
 *
 *   data-variant-id  variant to raise
 *   data-quantity    target total for that line
 *   data-missing     units still needed, used only when there is no line yet
 *
 * This deliberately does NOT use a <form>. The cards sit inside
 * #CartDrawer-Form, and a nested <form> is invalid HTML — the parser drops the
 * inner tags, which leaves the button submitting the outer cart form and
 * navigating to /cart. That is what closed the drawer.
 *
 * Instead it sets the line quantity through cart-drawer-items.updateQuantity(),
 * the same path the +/- steppers use: it PATCHes the cart and re-renders
 * .drawer__inner in place, so the drawer stays open.
 */
if (!customElements.get('cart-bulk-add')) {
  customElements.define(
    'cart-bulk-add',
    class CartBulkAdd extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', this.onClick);
      }

      get button() {
        return this.querySelector('button');
      }

      onClick(event) {
        if (!event.target.closest('button')) return;
        event.preventDefault();

        const target = parseInt(this.dataset.quantity, 10);
        const variantId = this.dataset.variantId;
        if (!target || !variantId) return;

        const cartItems = document.querySelector('cart-drawer-items');
        // The line's own quantity field tells us which line index to change.
        const input = cartItems?.querySelector(`.quantity__input[data-quantity-variant-id="${variantId}"]`);

        if (cartItems && input) {
          // Setting the line to the tier total tops it up by the difference:
          // a line already at 2 becomes 4, it does not become 6.
          const current = parseInt(input.value, 10) || 0;
          if (current >= target) return;

          // updateQuantity captures this input before re-rendering and then
          // compares the cart's new quantity against its value, reporting a
          // mismatch as "You can only add N of this item". The steppers avoid
          // that because the field is already updated by the time they call it,
          // so set it here too before handing over.
          input.value = target;

          cartItems.updateQuantity(input.dataset.index, target, event, null, variantId);
          return;
        }

        // No line to update. Add only what is still missing rather than the full
        // tier, otherwise an existing quantity is double counted and the cart
        // rejects it against the variant's maximum.
        const missing = parseInt(this.dataset.missing, 10);
        this.addToCart(missing > 0 ? missing : target, variantId);
      }

      addToCart(quantity, variantId) {
        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer) return;

        this.button?.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        const body = JSON.stringify({
          items: [{ id: variantId, quantity }],
          sections,
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_add_url}`, { ...fetchConfig('javascript'), ...{ body } })
          .then((response) => response.json())
          .then((parsed) => {
            if (parsed.status) {
              const errors = document.getElementById('CartDrawer-CartErrors');
              if (errors) errors.textContent = parsed.description || parsed.message;
              return;
            }
            cartDrawer.renderContents(parsed);
          })
          .catch((e) => console.error(e))
          .finally(() => {
            this.button?.classList.remove('loading');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }
    }
  );
}
